// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";

// Components
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Alert from "src/components/shared/alerts/Alert/Alert";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";

// Styles
import {
  ModalCustomCancel,
  ModalCustomDeleteSolid,
} from "src/styles/mui/muiStylesHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Services
import { getProgressionAnalysisForExams } from "src/services/exams/api";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";

type DeleteExamModalType = {
  open: boolean;
  patientId: string;
  selectedIds: Array<string>;
  onClose: () => void;
  onConfirm: () => void;
};

const DeleteExamModal = ({
  open,
  patientId,
  selectedIds,
  onClose,
  onConfirm,
}: DeleteExamModalType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();
  const { getToken } = useTokenAccess();

  const [progressionAnalysisCount, setProgressionAnalysisCount] = useState(0);

  // refetch the related progression analyses every time the patient, clinic, or selected exams change
  const fetchAnalyses = useCallback(async () => {
    const token = await getToken();
    getProgressionAnalysisForExams(
      token,
      clinicInfo?.id,
      patientId,
      selectedIds
    ).then((res) => {
      setProgressionAnalysisCount(res.length);
    });
  }, [clinicInfo?.id, patientId, selectedIds, getToken]);

  useEffect(() => {
    if (open) {
      fetchAnalyses();
    }
  }, [fetchAnalyses, open]);

  return (
    <DefaultModal
      isOpen={open}
      onClose={onClose}
      title={t("Delete Exam", {
        count: selectedIds?.length,
      })}
      maxWidth="xs"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
          }}
        >
          <Button sx={ModalCustomCancel} onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button sx={ModalCustomDeleteSolid} onClick={onConfirm}>
            {t("Delete")}
          </Button>
        </Grid>
      }
    >
      <Grid p="16px" display="flex" flexDirection="column" gap="20px">
        {progressionAnalysisCount > 0 && (
          <Alert
            variant="warning"
            alertContent={
              progressionAnalysisCount >= 1 && selectedIds.length === 1
                ? t(
                    "This exam is associated with a progression analysis report. Deleting it could affect the integrity of the report."
                  )
                : t(
                    "One or more of the selected exams are associated with a progression analysis report. Deleting them could affect the integrity of the report."
                  )
            }
          />
        )}
        <Typography fontSize="14px">
          {t("Are you sure you want to delete the selected exam?", {
            count: selectedIds?.length,
          })}
        </Typography>
      </Grid>
    </DefaultModal>
  );
};

export default DeleteExamModal;
